
// src/components/SSISymptomQuestionPanel.js
import React, { useState, useRef, useCallback, useContext } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon } from '@heroicons/react/solid';
// import { ChevronLeftIcon, ChevronRightIcon, MicrophoneIcon, StopIcon } from '@heroicons/react/solid';
import { auth } from '../../../../firebase';
import InfoTooltip from '../../../../components/InfoTooltip';
import DictationTextArea from '../../../../components/DictationTextArea';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { useSectionExitRegeneration } from '../../../../hooks/useSectionExitRegeneration';
import SsiGuidelineSources from './ssiGuidelineSources';

/**
 * In-context definitions for the five adulthood settings chips.
 *
 * Shown from a "?" next to "Settings in which this symptom occurs" so
 * clinicians know what each chip covers. There is deliberately no popover
 * title: the Domains tooltip has one ("About this section"), but this
 * trigger is "?"-only. Sources match the Domains tooltip via
 * SsiGuidelineSources.
 */
const settingsOccursTooltip = (
  <div>
    <p className="mb-2">Familial - Home life and close family environments, e.g., home, relatives' houses.</p>
    <p className="mb-2">Social - With friends or peer groups, e.g., parties, casual meetups, group outings.</p>
    <p className="mb-2">Occupational - Work or professional environments, e.g., the office, job sites, remote working, business travel.</p>
    <p className="mb-2">Educational - Academic or training environments, e.g., school, college, university, classrooms, lecture halls, labs, online coursework, training seminars.</p>
    <p className="mb-2">Community - Public spaces, civic environments, and local groups, e.g., grocery stores, places of worship, sports fields/clubs, community centres, hospitals, airports.</p>
    <SsiGuidelineSources />
  </div>
);

/**
 * Mapping from internal form keys to user-friendly scale display names.
 * Different healthcare providers may configure different ADHD rating scales,
 * so this mapping helps clinicians understand which instrument the data came from.
 */
const SCALE_DISPLAY_NAMES = {
  // Patient self-report scales
  'baarsIVCurrent': 'BAARS-IV Adulthood',
  'baarsIVChildhood': 'BAARS-IV Childhood',
  'asrs': 'ASRS',
  // Informant report scales  
  'baarsIVCurrentInformant': 'BAARS-IV Adulthood - Informant',
  'baarsIVChildhoodInformant': 'BAARS-IV Childhood - Informant',
  'asrsOAdulthoodInformant': 'ASRS-O - Informant',
  'snapIVChildhoodInformant': 'SNAP-IV Childhood - Informant',
};

export default function SSISymptomQuestionPanel({
  currentStageTitle,
  currentSectionTitle,
  currentSectionNumber,
  questions,
  question,
  index,
  selections,
  notes,
  settings,
  activeRecording,
  handleSelection,
  handleNoteChange,
  handleRecording,
  handleSettingsChange,
  jumpToQuestion,
  patientDataModel,
  fetchPatientData,
  updatePatientFirestore,
  patientId,
  navigate,
  textAreaRefs,
  informantsData,
  canEdit,
  // Optional path overrides for the read-only SSI snapshot viewer so
  // "Go to Domains" stays inside /ssi-snapshot/... rather than the live proforma.
  proformaNavOverride = null,
}) {

  const { flushPendingUpdates } = useContext(PatientDataContext);

  // Track whether any symptom data has been modified since the panel mounted.
  // Used by both the exit-regeneration hook (for unmanaged exits) and the
  // managed handleNextSection flow to skip unnecessary Cloud Function calls
  // when nothing has actually changed.
  const [hasChanges, setHasChanges] = useState(false);

  // Safety-net: fires regeneration on unmount if the clinician exits via
  // breadcrumb/topnav without reaching the "Go to Domains" button. Also
  // registers dirty state in the navigation guard so the confirmation
  // dialog appears when the SSI module was previously completed.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // Function to find the appropriate informant ID for a given life stage
  const getInformantIdForLifeStage = (lifeStage) => {

    // Look through all informants to find one assigned for this life stage
    for (const informantId in informantsData) {
      const informant = informantsData[informantId];

      if (informant.informantForLifeStage && informant.informantForLifeStage.includes(lifeStage)) {
        return informantId;
      }
    }

    console.log(`No informant found for ${lifeStage}`);
    return null;
  };

  // Get form data from patient responses
  // Returns the answer value, question text, and which scale/form the data came from
  const getPatientFormValue = (dsmCode, ageGroup) => {
    // Candidate forms in order of preference for each age group
    // Some organisations may use ASRS instead of BAARS-IV for Adulthood
    const candidateKeys = ageGroup === 'Adulthood'
      ? ['asrs', 'baarsIVCurrent']  // ASRS preferred for some orgs, fallback to BAARS-IV
      : ['baarsIVChildhood'];        // Only BAARS-IV for childhood currently

    // Find the first available form that has data
    let formData = null;
    let usedFormKey = null;
    for (const key of candidateKeys) {
      if (patientDataModel?.formData?.[key]) {
        formData = patientDataModel.formData[key];
        usedFormKey = key;
        break;
      }
    }

    if (!formData) {
      console.log(`No form data found for patient in ${candidateKeys.join(' or ')}`);
      return { value: '', questionText: '', formKey: null };
    }

    // Look for matching question using prefillsToProviderVarNames metadata
    // This metadata is defined in form_data_model.py and stored with the patient's form data
    for (const sectionKey in formData.formSubsections || {}) {
      const section = formData.formSubsections[sectionKey];

      for (const questionKey in section.questions || {}) {
        const question = section.questions[questionKey];

        // Check if this question maps to our DSM code
        if (question.prefillsToProviderVarNames &&
          question.prefillsToProviderVarNames.includes(dsmCode)) {

          // Look through subquestions
          for (const subqKey in question.subquestions || {}) {
            const subq = question.subquestions[subqKey];

            // Check if this matches our age group (e.g., "Adulthood" in variable name)
            if (subq.serviceUserVarName &&
              subq.serviceUserVarName.includes(ageGroup)) {
              return {
                value: subq.answer?.value || '',
                questionText: question.questionTitle || '',
                formKey: usedFormKey
              };
            }
          }
        }
      }
    }

    console.log(`No matching data found for DSM code ${dsmCode} in ${ageGroup}`);
    return { value: '', questionText: '', formKey: usedFormKey };
  };

  // Get form data from informant responses
  // Returns the answer value, question text, informant ID, and which scale/form the data came from
  const getInformantFormValue = (dsmCode, ageGroup) => {
    // Find the appropriate informant
    const informantId = getInformantIdForLifeStage(ageGroup);

    if (!informantId) {
      console.log(`No informant ID found for ${ageGroup}`);
      return { value: '', questionText: '', informantId: null, formKey: null };
    }

    const informant = informantsData[informantId];

    if (!informant) {
      return { value: '', questionText: '', informantId: null, formKey: null };
    }

    // Determine which form(s) to use based on organisation policy
    // Adulthood: ASRS-O (preferred for some orgs) or BAARS-IV Current
    // Childhood: SNAP-IV (preferred for some orgs) or BAARS-IV Childhood
    const candidateKeys = ageGroup === 'Adulthood'
      ? ['asrsOAdulthoodInformant', 'baarsIVCurrentInformant']
      : ['snapIVChildhoodInformant', 'baarsIVChildhoodInformant'];

    // Pick the first available form and track which one was used
    let formData = null;
    let usedFormKey = null;
    for (const key of candidateKeys) {
      if (informant.formData?.[key]) {
        formData = informant.formData[key];
        usedFormKey = key;
        break;
      }
    }

    if (!formData) {
      console.log(`No form data found for informant in ${candidateKeys.join(' or ')}`);
      return { value: '', questionText: '', informantId, formKey: null };
    }

    // Look for matching question using prefillsToProviderVarNames metadata
    // This metadata is defined in form_data_model.py / informant_form_data_model.py
    for (const sectionKey in formData.formSubsections || {}) {
      const section = formData.formSubsections[sectionKey];

      for (const questionKey in section.questions || {}) {
        const question = section.questions[questionKey];

        // Check if this question maps to our DSM code
        if (question.prefillsToProviderVarNames &&
          question.prefillsToProviderVarNames.includes(dsmCode)) {

          for (const subqKey in question.subquestions || {}) {
            const subq = question.subquestions[subqKey];

            // Check if this matches our age group (e.g., "Adulthood" in variable name)
            if (subq.serviceUserVarName &&
              subq.serviceUserVarName.includes(ageGroup)) {

              return {
                value: subq.answer?.value || '',
                questionText: question.questionTitle || '',
                informantId,
                formKey: usedFormKey
              };
            }
          }
        }
      }
    }

    console.log(`No matching informant data found for DSM code ${dsmCode} in ${ageGroup}`);
    return { value: '', questionText: '', informantId, formKey: usedFormKey };
  };

  // Get the DSM code for the current question
  const dsmCode = question?.DSM_code || '';

  // Get data for each scenario
  const patientAdulthood = getPatientFormValue(dsmCode, 'Adulthood');
  const patientChildhood = getPatientFormValue(dsmCode, 'Childhood');
  const informantAdulthood = getInformantFormValue(dsmCode, 'Adulthood');
  const informantChildhood = getInformantFormValue(dsmCode, 'Childhood');

  // console.log('Final patient adulthood data:', patientAdulthood);
  // console.log('Final patient childhood data:', patientChildhood);
  // console.log('Final informant adulthood data:', informantAdulthood);
  // console.log('Final informant childhood data:', informantChildhood);

  // Get informant relationship info for display
  const getInformantRelationship = (informantId) => {
    if (!informantId) {
      console.log('No informant ID provided for relationship lookup');
      return 'No informant';
    }

    const informant = informantsData[informantId];

    if (!informant) {
      console.log(`No data found for informant ${informantId}`);
      return 'Unknown';
    }

    // Check both possible locations for the relationship info
    const relationship =
      informant.informantRelationshipToPatient ||
      'Not available';

    return relationship;
  };

  const adulthoodInformantDisplay = getInformantRelationship(informantAdulthood.informantId);
  const childhoodInformantDisplay = getInformantRelationship(informantChildhood.informantId);

  console.log('Adulthood informant display:', adulthoodInformantDisplay);
  console.log('Childhood informant display:', childhoodInformantDisplay);

  // Define the settings options
  const settingsOptions = [
    "Familial",
    "Social",
    "Occupational",
    "Educational",
    "Community"
  ];

  const handleSelectionWithFirestore = (index, type, value) => {
    if (!canEdit) return;

    // Check if we're clicking an already selected button (toggling off)
    const isUnselecting = selections[index][type] === value;

    // Call the parent component's selection handler
    handleSelection(index, type, value);

    const fieldToUpdate = type === "adulthood" ? 'symptomPresent_Adulthood' :
      type === "childhood" ? 'symptomPresent_Childhood' : null;

    // Update Firestore with null if unselecting, otherwise with the new value
    updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        sections: {
          [currentSectionNumber]: {
            questions: {
              [index + 1]: {
                [fieldToUpdate]: isUnselecting ? null : value,
              }
            }
          },
        },
      },
    });

    // If unselecting Yes for adulthood, clear the settings selections
    if (type === "adulthood" && isUnselecting && value === true) {
      handleSettingsChange(index, []);
    }

    setHasChanges(true);
    fetchPatientData(patientId);
  };

  const debounceTimeoutRef = useRef(null);

  // Persists the current note value to Firestore on blur (debounced).
  //
  // IMPORTANT: this handler intentionally does NOT call setHasChanges(true).
  // Dirty-tracking is handled exclusively by the onChange handler on the
  // DictationTextArea. The blur handler's sole job is data persistence.
  // Combining the two caused false-positive dirty checks: when the clinician
  // navigated between questions, the DictationTextArea's unmount safety-net
  // fired onBlur even though no text was edited, which incorrectly flagged
  // the page as dirty and triggered the navigation guard.
  const handleNoteBlur = async (index, type) => {
    if (!canEdit) return;

    // Skip the write if the note hasn't changed from what Firestore already
    // has (avoids a wasted write when the user merely focused the textarea
    // without typing, or when the unmount safety-net fires on navigation).
    const currentValue = notes[index]?.[type.toLowerCase()] ?? '';
    const storedValue = questions[index]?.[`clinicianNotes_${type}`] || '';
    if (currentValue === storedValue) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      await updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              questions: {
                [index + 1]: {
                  [`clinicianNotes_${type}`]: notes[index][type.toLowerCase()]
                }
              }
            }
          }
        }
      });
    }, 500); // Debounce delay
  };

  // Handle selecting a setting
  const handleSettingSelection = (setting) => {
    if (!canEdit) return;

    const currentSettings = settings[index].adulthood || [];
    const newSettings = currentSettings.includes(setting)
      ? currentSettings.filter(s => s !== setting)  // Remove if already selected
      : [...currentSettings, setting];              // Add if not selected

    handleSettingsChange(index, newSettings);
    setHasChanges(true);
  };

  // Initialize selectedExamples as an array of Sets for each question
  const [selectedExamples, setSelectedExamples] = useState(() =>
    questions.map(q => ({
      adulthood: new Set(q.examples_Adulthood || []),
      childhood: new Set(q.examples_Childhood || [])
    }))
  );

  // Handle checkbox changes
  const handleCheckboxChange = (type, example) => {
    if (!canEdit) return;

    setSelectedExamples(prev => {
      const updatedSet = new Set(prev[index][type]);
      if (updatedSet.has(example)) {
        updatedSet.delete(example);
      } else {
        updatedSet.add(example);
      }

      // Return the updated set for the specific question
      const updatedExamples = [...prev];
      updatedExamples[index] = { ...prev[index], [type]: updatedSet };

      // Update Firestore with the current selections
      const firestoreUpdate = {
        examples_Adulthood: Array.from(updatedExamples[index].adulthood),
        examples_Childhood: Array.from(updatedExamples[index].childhood)
      };

      updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              questions: {
                [index + 1]: firestoreUpdate
              }
            }
          }
        }
      }).then(() => {
        fetchPatientData(patientId);
      });

      setHasChanges(true);
      return updatedExamples;
    });
  };

  // Fires off data persistence and conditional AI generation, then
  // navigates immediately.  The Cloud Function call is chained to the
  // flush Promise so it fires as soon as Firestore confirms the write.
  const handleNextSection = useCallback((route) => {
    const user = auth.currentUser;
    if (!user || !user.uid) return;

    markRegenHandled();

    const dataReady = flushPendingUpdates({ noTimeout: true });

    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['2']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes(currentSectionTitle);

    if (canEdit && (hasChanges || !isGenerated)) {
      TriggerGenerateDocumentSection(patientId, 'Semi-Structured Interview', currentSectionTitle, [], dataReady);
    }

    navigate(route);
  }, [patientId, navigate, currentSectionTitle, canEdit, flushPendingUpdates,
      markRegenHandled, hasChanges, patientDataModel]);

  const handleGoToDomains = useCallback(() => {
    if (canEdit) {
      updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              status: 'completed',
              progress: 100,
            },
          },
        },
      });
    }
    const domainsPath =
      proformaNavOverride?.domainsPath ||
      `/patient/${patientId}/proforma/semi-structured-interview/domains`;
    handleNextSection(domainsPath);
  }, [
    canEdit,
    patientId,
    currentSectionNumber,
    updatePatientFirestore,
    handleNextSection,
    proformaNavOverride,
  ]);


  return (
    <div key={index} id={`question-${index}`} className="bg-white p-6 rounded-lg mb-6">

      {/* Primary navigation: two main CTAs (back/forward in sequence).
          Skip links below provide a subtle shortcut to jump across sections
          without cluttering the button row. */}
      <div className="flex justify-between mb-3">
        <div className="flex flex-col items-start">
          {index === 0 ? (
            <button
              onClick={() => {
                navigate(`/patient/${patientId}`);
              }}
              className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
              aria-label="Back to Patient Overview"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">Back to Patient Overview</span>
            </button>
          ) : (
            <button
              onClick={() => jumpToQuestion(index - 1)}
              className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
              disabled={!!activeRecording}
              aria-label="Previous question"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">Previous Symptom</span>
            </button>
          )}
        </div>

        <div className="flex flex-col items-end">
          <button
            onClick={() => {
              if (index === questions.length - 1) {
                handleGoToDomains();
              } else {
                jumpToQuestion(index + 1);
              }
            }}
            disabled={!!activeRecording}
            className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md transition duration-100 bg-violet-900 text-white hover:bg-violet-800"
            aria-label={index === questions.length - 1 ? "Go to Domains" : "Next Symptom"}
          >
            <span className="font-bold">
              {index === questions.length - 1
                ? 'Go to Domains'
                : 'Next Symptom'}
            </span>
            <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
          </button>
          {index < questions.length - 1 && (
            <button
              onClick={handleGoToDomains}
              className="flex items-center text-xs text-gray-500 hover:text-violet-700 font-medium hover:underline transition-all duration-200 mt-1.5 group"
              aria-label="Go to Domains"
            >
              <span>Go to Domains</span>
              <ArrowRightIcon className="w-3 h-3 ml-1 text-gray-400 group-hover:text-violet-700 transition-all duration-200" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-3">
        <h2 className="flex items-center gap-2 text-gray-900 text-xl font-semibold leading-9 break-words">
          {question.DSM_symptom}
        </h2>
      </div>

      {/* ADULTHOOD */}
      <div className="flex mb-4">
        <div className="w-full text-left">
          <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-center gap-2 inline-flex">
            <div className="self-stretch flex-col justify-start items-center flex">
              <div className="self-stretch justify-start items-center gap-4 inline-flex">
                <div className="grow shrink basis-0 self-stretch justify-start items-center gap-1 flex">
                  <div className="text-slate-700 text-xl font-semibold leading-loose">Adulthood (for at least 6 months)</div>
                </div>
                <div className="self-stretch justify-center items-center flex">
                  <div className="h-10 justify-start items-center flex">
                    <div className="grow shrink basis-0 h-9 justify-center items-center gap-3 flex">
                      <div className="flex-col justify-center items-center inline-flex">
                        <div className="text-slate-700 text-lg font-medium leading-normal">Symptom present:</div>
                      </div>
                      <div className="grow shrink basis-0 h-9 justify-start items-center gap-3 flex">
                        {/* The visible label is only "Yes" or "No". The life
                            period sits in a nearby heading that a screen reader
                            does not attach to the button. The accessible name
                            has to say adulthood or childhood, or the two pairs
                            sound identical and a childhood answer can be saved
                            as the adulthood one. Those answers feed the DSM-5
                            criteria. Keep each pair's name on its own period. */}
                        <button
                          onClick={() => handleSelectionWithFirestore(index, 'adulthood', true)}
                          className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                            ${selections[index].adulthood === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white'} 
                            ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                            transition-all duration-300`}
                          disabled={!!activeRecording || !canEdit}
                          aria-label="Symptom present in adulthood: Yes"
                        >
                          <div className="px-0.5 justify-center items-center flex">
                            <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                          </div>
                        </button>
                        <button
                          onClick={() => handleSelectionWithFirestore(index, 'adulthood', false)}
                          className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                            ${selections[index].adulthood === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} 
                            ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                            transition-all duration-300`}
                          disabled={!!activeRecording || !canEdit}
                          aria-label="Symptom present in adulthood: No"
                        >
                          <div className="px-0.5 justify-center items-center flex">
                            <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full gap-6">
              {/* Left side - Pre-filled Information */}
              <div className="w-1/3 flex flex-col gap-2">
                {/* Patient Self-report */}
                <div className="flex flex-col gap-2">
                  <label className="text-slate-700 text-sm font-medium flex items-center gap-2">
                    Patient Self-report
                    {/* Scale source badge - indicates which ADHD scale the data came from */}
                    {patientAdulthood.formKey && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 ml-auto">
                        {SCALE_DISPLAY_NAMES[patientAdulthood.formKey] || patientAdulthood.formKey}
                      </span>
                    )}
                  </label>
                  <div className="w-full p-4 rounded-lg border bg-gray-100 text-base">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700 text-sm font-medium">Response:</span>
                      <span className="text-gray-700 text-sm">
                        {patientAdulthood.value || 'Not provided'}
                      </span>
                      <InfoTooltip
                        content={patientAdulthood.questionText}
                        label={`Information about this question`}
                        maxWidth="250px"
                        className="ml-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Only show adulthood informant section if informant exists */}
                {informantAdulthood.informantId && (
                  <>
                    <div className="border-t border-gray-200 my-4"></div>

                    {/* Informant Report for Adulthood */}
                    <div className="flex flex-col gap-2">
                      <label className="text-slate-700 text-sm font-medium flex items-center gap-2 flex-wrap">
                        Informant Report ({adulthoodInformantDisplay})
                        {/* Scale source badge - indicates which ADHD scale the data came from */}
                        {informantAdulthood.formKey && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 ml-auto">
                            {SCALE_DISPLAY_NAMES[informantAdulthood.formKey] || informantAdulthood.formKey}
                          </span>
                        )}
                      </label>
                      <div className="w-full p-4 rounded-lg border bg-gray-100 text-base">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700 text-sm font-medium">Response:</span>
                          <span className="text-gray-700 text-sm">
                            {informantAdulthood.value || 'Not provided'}
                          </span>
                          <InfoTooltip
                            content={informantAdulthood.questionText}
                            label={`Information about this question`}
                            maxWidth="250px"
                            className="ml-2"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="border-l border-gray-200 mx-2"></div>

              {/* Right side */}
              <div className="w-2/3 flex flex-col">

                {/* Settings section */}
                <div className="w-full mt-4 transition-opacity duration-300"
                  style={{ opacity: selections[index].adulthood === true ? 1 : 0.5 }}>
                  <div className="">
                    {/* "?" sits beside the heading, not inside it, so screen
                        readers still announce the heading as the phrase only. */}
                    <div className="flex items-center gap-1 mb-3">
                      <h3 className="text-slate-700 text-sm font-bold">
                        Settings in which this symptom occurs
                      </h3>
                      <InfoTooltip
                        content={settingsOccursTooltip}
                        label="More information about settings in which this symptom occurs"
                        maxWidth="380px"
                        side="bottom"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {settingsOptions.map((setting) => {
                        const isSelected = settings[index].adulthood?.includes(setting);

                        return (
                          <button
                            key={setting}
                            onClick={() => handleSettingSelection(setting)}
                            disabled={selections[index].adulthood !== true || !canEdit}
                            className={`
                                px-4 py-2 rounded-lg text-sm font-semibold
                                transition-all duration-200 
                                ${!canEdit ? 'opacity-75' : ''} 
                                ${selections[index].adulthood !== true
                                ? 'cursor-not-allowed bg-gray-100 text-gray-400 border border-gray-300'
                                : isSelected
                                  ? 'bg-violet-300 border-2 border-violet-500 text-violet-900'
                                  : 'bg-violet-100 hover:bg-violet-200 border border-violet-200 text-gray-700'
                              }
                            `}
                          >
                            {setting}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                {/* Examples checklist */}
                <div className="flex flex-col gap-2">
                  <label className="text-slate-700 text-sm font-semibold">
                    Examples
                  </label>
                  <div className="flex flex-col">
                    {(question.examplesOptions_Adulthood || []).map((example, i) => (
                      <label
                        key={i}
                        className="flex items-start gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="checkbox mt-1"
                          checked={selectedExamples[index].adulthood.has(example)}
                          onChange={() => handleCheckboxChange('adulthood', example)}
                          disabled={!canEdit}
                          aria-label={`Example: ${example}`}
                        />
                        <span className="text-slate-700 text-sm font-medium">
                          {example}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                {/* Clinician Notes */}
                <div className="grow shrink-0 self-stretch flex-col justify-end items-start gap-2 inline-flex">
                  <label className="text-slate-700 text-sm font-semibold">
                    Clinical Notes
                  </label>
                  <DictationTextArea
                    ref={textAreaRefs.current[index]?.adulthood}
                    value={notes[index].adulthood}
                    onChange={(val) => { handleNoteChange(index, 'Adulthood', val); setHasChanges(true); }}
                    onBlur={() => handleNoteBlur(index, 'Adulthood')}
                    canEdit={canEdit}
                    disabled={!canEdit || (activeRecording && (activeRecording.index !== index || activeRecording.type !== 'adulthood'))}
                    ariaLabel="Clinician notes for adulthood"
                    minHeight="15rem"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CHILDHOOD */}
      <div className="flex mb-4">
        <div className="w-full text-left">
          <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-center gap-2 inline-flex">
            <div className="self-stretch flex-col justify-start items-center flex">
              <div className="self-stretch justify-start items-center gap-4 inline-flex">
                <div className="grow shrink basis-0 self-stretch justify-start items-center gap-1 flex">
                  <div className="text-slate-700 text-xl font-semibold leading-loose">Childhood (under 12)</div>
                </div>
                <div className="self-stretch justify-center items-center flex">
                  <div className="h-10 justify-start items-center flex">
                    <div className="grow shrink basis-0 h-9 justify-center items-center gap-3 flex">
                      <div className="flex-col justify-center items-center inline-flex">
                        <div className=" text-slate-700 text-lg font-medium leading-normal">Symptom present:</div>
                      </div>
                      <div className="grow shrink basis-0 h-9 justify-start items-center gap-3 flex">
                        <button
                          onClick={() => handleSelectionWithFirestore(index, 'childhood', true)}
                          className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                            ${selections[index].childhood === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white'} 
                            ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                            transition-all duration-300`}
                          disabled={!!activeRecording || !canEdit}
                          aria-label="Symptom present in childhood: Yes"
                        >
                          <div className="px-0.5 justify-center items-center flex">
                            <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                          </div>
                        </button>
                        <button
                          onClick={() => handleSelectionWithFirestore(index, 'childhood', false)}
                          className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                            ${selections[index].childhood === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} 
                            ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                            transition-all duration-300`}
                          disabled={!!activeRecording || !canEdit}
                          aria-label="Symptom present in childhood: No"
                        >
                          <div className="px-0.5 justify-center items-center flex">
                            <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="flex w-full gap-6">
              {/* Left side - Pre-filled Information */}
              <div className="w-1/3 flex flex-col gap-2">
                {/* Patient Self-report */}
                <div className="flex flex-col gap-2">
                  <label className="text-slate-700 text-sm font-medium flex items-center gap-2">
                    Patient Self-report
                    {/* Scale source badge - indicates which ADHD scale the data came from */}
                    {patientChildhood.formKey && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 ml-auto">
                        {SCALE_DISPLAY_NAMES[patientChildhood.formKey] || patientChildhood.formKey}
                      </span>
                    )}
                  </label>
                  <div className="w-full p-4 rounded-lg border bg-gray-100 text-base">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700 text-sm font-medium">Response:</span>
                      <span className="text-gray-700 text-sm">
                        {patientChildhood.value || 'Not provided'}
                      </span>
                      <InfoTooltip
                        content={patientChildhood.questionText}
                        label={`Information about this question`}
                        maxWidth="250px"
                        className="ml-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Only show childhood informant section if informant exists */}
                {informantChildhood.informantId && (
                  <>
                    <div className="border-t border-gray-200 my-4"></div>

                    {/* Informant Report for Childhood */}
                    <div className="flex flex-col gap-2">
                      <label className="text-slate-700 text-sm font-medium flex items-center gap-2 flex-wrap">
                        Informant Report ({childhoodInformantDisplay})
                        {/* Scale source badge - indicates which ADHD scale the data came from */}
                        {informantChildhood.formKey && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 ml-auto">
                            {SCALE_DISPLAY_NAMES[informantChildhood.formKey] || informantChildhood.formKey}
                          </span>
                        )}
                      </label>
                      <div className="w-full p-4 rounded-lg border bg-gray-100 text-base">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700 text-sm font-medium">Response:</span>
                          <span className="text-gray-700 text-sm">
                            {informantChildhood.value || 'Not provided'}
                          </span>
                          <InfoTooltip
                            content={informantChildhood.questionText}
                            label={`Information about this question`}
                            maxWidth="250px"
                            className="ml-2"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="border-l border-gray-200 mx-2"></div>

              {/* Right side */}
              <div className="w-2/3 flex flex-col">

                {/* Examples checklist */}
                <div className="flex flex-col gap-2">
                  <label className="text-slate-700 text-sm font-semibold">
                    Examples
                  </label>
                  <div className="flex flex-col">
                    {(question.examplesOptions_Childhood || []).map((example, i) => (
                      <label
                        key={i}
                        className="flex items-start gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="checkbox mt-1"
                          checked={selectedExamples[index].childhood.has(example)}
                          onChange={() => handleCheckboxChange('childhood', example)}
                          disabled={!canEdit}
                          aria-label={`Example: ${example}`}
                        />
                        <span className="text-slate-700 text-sm font-medium">
                          {example}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                {/* Clinician Notes */}
                <div className="grow shrink-0 self-stretch flex-col justify-end items-start gap-2 inline-flex">
                  <label className="text-slate-700 text-sm font-semibold">
                    Clinical Notes
                  </label>
                  <DictationTextArea
                    ref={textAreaRefs.current[index]?.childhood}
                    value={notes[index].childhood}
                    onChange={(val) => { handleNoteChange(index, 'Childhood', val); setHasChanges(true); }}
                    onBlur={() => handleNoteBlur(index, 'Childhood')}
                    canEdit={canEdit}
                    disabled={!canEdit || (activeRecording && (activeRecording.index !== index || activeRecording.type !== 'childhood'))}
                    ariaLabel="Clinician notes for childhood"
                    minHeight="15rem"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end mb-3">
        <div className="flex flex-col items-end">
          <button
            onClick={() => {
              if (index === questions.length - 1) {
                handleGoToDomains();
              } else {
                jumpToQuestion(index + 1);
              }
            }}
            disabled={!!activeRecording}
            className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md transition duration-100 bg-violet-900 text-white hover:bg-violet-800"
            aria-label={index === questions.length - 1 ? "Go to Domains" : "Next Symptom"}
          >
            <span className="font-bold">
              {index === questions.length - 1
                ? 'Go to Domains'
                : 'Next Symptom'}
            </span>
            <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
          </button>
          {index < questions.length - 1 && (
            <button
              onClick={handleGoToDomains}
              className="flex items-center text-xs text-gray-500 hover:text-violet-700 font-medium hover:underline transition-all duration-200 mt-1.5 group"
              aria-label="Go to Domains"
            >
              <span>Go to Domains</span>
              <ArrowRightIcon className="w-3 h-3 ml-1 text-gray-400 group-hover:text-violet-700 transition-all duration-200" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
